import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Space, Typography, Table, Tag } from 'antd';
import { Line } from '@ant-design/charts';
import dayjs from 'dayjs';
import request from '@/utils/request';

interface Overview {
  users: { creatorCount: number; activeCreatorCount: number; consumerCount: number };
  bundles: { total: number; published: number; pendingReview: number };
  purchases: { total: number; completed: number; exceptions: number };
  gifts: { total: number };
  finance: {
    grossRevenueCents: number;
    refundsCents: number;
    creatorEarningsCents: number;
    pendingWithdrawalCents: number;
  };
}

interface DailyRow {
  date: string;
  count: number;
  revenueCents: number;
}

interface Activity {
  id: number;
  action: string;
  userId: number | null;
  createdAt: string;
  details: unknown;
}

const fmtCents = (v: number) => `¥${(v / 100).toFixed(2)}`;

const MarketplaceDashboardPage: React.FC = () => {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);

  useEffect(() => {
    (async () => {
      const [o, d, a] = await Promise.all([
        request.get<Overview>('/admin/marketplace/overview'),
        request.get<DailyRow[]>('/admin/marketplace/daily-revenue', { params: { days: 14 } }),
        request.get<Activity[]>('/admin/marketplace/recent-activity'),
      ]);
      setOverview(o.data as unknown as Overview);
      setDaily((d.data as unknown as DailyRow[]).map((r) => ({
        ...r,
        revenueYuan: r.revenueCents / 100,
      })) as unknown as DailyRow[]);
      setActivity(a.data as unknown as Activity[]);
    })();
  }, []);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        市场看板
      </Typography.Title>

      {overview && (
        <>
          <Row gutter={16}>
            <Col span={6}>
              <Card>
                <Statistic title="创作者" value={overview.users.creatorCount} suffix={`活跃 ${overview.users.activeCreatorCount}`} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="消费者" value={overview.users.consumerCount} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="已发布套图"
                  value={overview.bundles.published}
                  suffix={`/ 待审核 ${overview.bundles.pendingReview}`}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="订单总数"
                  value={overview.purchases.total}
                  suffix={`完成 ${overview.purchases.completed} 异常 ${overview.purchases.exceptions}`}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Card>
                <Statistic title="总营收" value={fmtCents(overview.finance.grossRevenueCents)} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="退款"
                  value={fmtCents(overview.finance.refundsCents)}
                  valueStyle={{ color: '#DC2626' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="创作者累计收益"
                  value={fmtCents(overview.finance.creatorEarningsCents)}
                  valueStyle={{ color: '#15803D' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="待提现金额"
                  value={fmtCents(overview.finance.pendingWithdrawalCents)}
                  valueStyle={{ color: '#F59E0B' }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      <Card title="14 日营收趋势（元）">
        {daily.length > 0 ? (
          <Line
            data={daily.map((d) => ({ date: d.date, yuan: d.revenueCents / 100 }))}
            xField="date"
            yField="yuan"
            height={260}
            color="#B45309"
            point={{ size: 4 }}
          />
        ) : (
          <Typography.Text type="secondary">暂无数据</Typography.Text>
        )}
      </Card>

      <Card title="最近活动">
        <Table
          rowKey="id"
          dataSource={activity}
          pagination={false}
          columns={[
            { title: '时间', dataIndex: 'createdAt', render: (v) => dayjs(v).format('MM-DD HH:mm:ss') },
            { title: '事件', dataIndex: 'action', render: (v) => <Tag>{v}</Tag> },
            { title: '用户 ID', dataIndex: 'userId', render: (v) => v ?? '-' },
          ]}
        />
      </Card>
    </Space>
  );
};

export default MarketplaceDashboardPage;
